import type { EventType } from '@dairy/shared';
import { live, type DomainEvent } from './types';

/**
 * Total order of an animal's events. Same-day events follow the natural sequence of the day
 * (a cow calves, is seen in heat, is served, is checked), then device save time, then id.
 */
const TYPE_RANK: Record<EventType, number> = {
  CALVING: 0,
  ABORTION: 1,
  DRY_OFF: 2,
  HEAT: 3,
  SERVICE: 4,
  PREG_CHECK: 5,
  TREATMENT: 6,
  VACCINATION: 6,
  DEWORMING: 6,
  WEIGHT: 6,
  NOTE: 6,
};

export function compareEvents(a: DomainEvent, b: DomainEvent): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  const r = TYPE_RANK[a.type] - TYPE_RANK[b.type];
  if (r !== 0) return r;
  const ca = a.clientCreatedAt ?? '';
  const cb = b.clientCreatedAt ?? '';
  if (ca !== cb) return ca < cb ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Live events of one animal, sorted. */
export function eventsOf(events: readonly DomainEvent[], animalId: string): DomainEvent[] {
  return live(events)
    .filter((e) => e.animalId === animalId)
    .sort(compareEvents);
}

/** Live events sorted (any animal). */
export function sortedEvents(events: readonly DomainEvent[]): DomainEvent[] {
  return live(events).sort(compareEvents);
}

export function isAfter(e: DomainEvent, ref: DomainEvent): boolean {
  return compareEvents(e, ref) > 0;
}

/** Group live events by animal, each list sorted. */
export function groupEventsByAnimal(events: readonly DomainEvent[]): Map<string, DomainEvent[]> {
  const map = new Map<string, DomainEvent[]>();
  for (const e of sortedEvents(events)) {
    const list = map.get(e.animalId);
    if (list) list.push(e);
    else map.set(e.animalId, [e]);
  }
  return map;
}

/** Latest event of `type` on or before `date` (sorted input). */
export function latestOnOrBefore(
  sorted: readonly DomainEvent[],
  type: EventType,
  date: string,
): DomainEvent | undefined {
  let found: DomainEvent | undefined;
  for (const e of sorted) {
    if (e.date > date) break;
    if (e.type === type) found = e;
  }
  return found;
}
